import React from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import { Text, Flex, Box } from '@atlaskit/primitives/compiled';
import type { AvatarProps } from '@atlaskit/avatar-group';
import ProgressBar from '@atlaskit/progress-bar';

import type { Approver } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import { ApprovalStatusButton } from './ApprovalStatusButton.tsx';

const styles = cssMap({
	fullWidth: {
		width: '100%',
	},
	progressRow: {
		width: '192px',
	},
	textContainer: {
		flexShrink: 0,
		textAlign: 'start',
	},
	progressBarContainer: {
		flexGrow: 1,
		minWidth: '0px',
	},
});

const i18n = defineMessages({
	inProgressApprovalTitle: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.in-progress-title.non-final',
		defaultMessage: 'Approval in progress',
		description: 'Title for indicating the status of the approval process',
	},
	inProgressApprovalDescription: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.in-progress-description.non-final',
		defaultMessage: '{approvalCount}/{approverCount} approved',
		description:
			'Description for indicating how many approvers have approved the content. approvalCount is the number of approvals received, approverCount is the total number of approvals required.',
	},
	approvedApprovalTitle: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.approved-approval-title.non-final',
		defaultMessage: 'Approved by',
		description: 'Title for indicating the document approvers',
	},
	approvedApprovalDescription: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.approved-approval-description.non-final',
		defaultMessage: '{approver} and {count} {count, plural, one {other} other {others}}',
		description:
			'Description for indicating how many approvers have approved the content in the case of multiple. {approver} is the name of the approver, {count} is the number of other approvers where count is greater than 1.',
	},
	declinedApprovalTitle: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.declined-approval-title.non-final',
		defaultMessage: 'Approval declined',
		description: 'Title for indicating the status of the approval process',
	},
});

export enum InProgressApprovalButtonVariant {
	IN_PROGRESS = 'in-progress',
	APPROVED = 'approved',
	DECLINED = 'declined',
}

export interface InProgressApprovalButtonProps {
	onClick?: () => void;
	approvalCount: number;
	approverCount: number;
	approvers: Approver[];
	variant?: InProgressApprovalButtonVariant;
}

export const InProgressApprovalButton = ({
	onClick,
	approvalCount,
	approverCount,
	approvers,
	variant = InProgressApprovalButtonVariant.IN_PROGRESS,
}: InProgressApprovalButtonProps) => {
	const { formatMessage } = useIntl();

	const title =
		variant === InProgressApprovalButtonVariant.IN_PROGRESS
			? formatMessage(i18n.inProgressApprovalTitle)
			: variant === InProgressApprovalButtonVariant.APPROVED
				? formatMessage(i18n.approvedApprovalTitle)
				: formatMessage(i18n.declinedApprovalTitle);

	const description =
		variant === InProgressApprovalButtonVariant.IN_PROGRESS ||
		variant === InProgressApprovalButtonVariant.DECLINED
			? formatMessage(i18n.inProgressApprovalDescription, { approvalCount, approverCount })
			: getApprovedDescription(approvers, formatMessage);

	return (
		<ApprovalStatusButton onClick={onClick} avatarProps={getAvatarData(approvers)}>
			<Flex direction="column" alignItems="start" gap="space.025" xcss={styles.fullWidth}>
				<Text size="small" color="color.text.subtlest">
					{title}
				</Text>
				<Flex direction="row" gap="space.100" alignItems="center" xcss={styles.progressRow}>
					<Box xcss={styles.textContainer}>
						<Text size="medium">{description}</Text>
					</Box>
					{variant === InProgressApprovalButtonVariant.IN_PROGRESS && (
						<Box xcss={styles.progressBarContainer}>
							<ProgressBar
								appearance="success"
								value={approvalCount / approverCount}
								ariaLabel={formatMessage(i18n.inProgressApprovalDescription, {
									approvalCount,
									approverCount,
								})}
							/>
						</Box>
					)}
				</Flex>
			</Flex>
		</ApprovalStatusButton>
	);
};

function getAvatarData(approvers: Approver[]): AvatarProps[] {
	return approvers.map((approver) => {
		const status =
			approver.status === 'REVIEWER_APPROVED'
				? 'approved'
				: approver.status === 'REVIEWER_DENIED'
					? 'declined'
					: undefined;

		return {
			appearance: 'circle',
			src: approver.avatarUrl,
			status,
			name: undefined, // Atlaskit requires no name for decorative non-button avatars
			size: 'small',
		} as AvatarProps;
	});
}

const getApprovedDescription = (
	approvers: Approver[],
	formatMessage: (message: MessageDescriptor, values: Record<string, string | number>) => string,
): string => {
	if (approvers.length === 1) {
		return approvers[0].name;
	}

	return formatMessage(i18n.approvedApprovalDescription, {
		approver: approvers[0].name,
		count: approvers.length - 1,
	});
};
